<template>
  <Dashboard>
    <div class="flex flex-col gap-6">
      <h1 class="text-text-primary text-[28px] font-medium flex-grow flex items-center gap-2">
        {{ $t('common.matches') }} <Info :title="$t('common.matches')" :description="$t('coach.matches_page.info_description')" />
      </h1>

      <div>
        <nav class="w-full flex flex-row gap-9 relative border-b border-gray-800">
          <div
            ref="historyDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('history')"
          >
            <img
              src="../../assets/icons/history.svg"
              :class="[activeTab === 'history' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300"
              :alt="$t('coach.alt.info_icon')"
            />
            <h3
              class="leading-5 duration-300"
              :class="[
                activeTab === 'history' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              {{ $t('coach.shared.match_history') }}
            </h3>
          </div>
          <div
            v-if="user.access_level > 1"
            ref="uploadsDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('uploads')"
          >
            <img
              src="../../assets/icons/upload.svg"
              :class="[activeTab === 'uploads' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300"
              :alt="$t('coach.alt.stats_icon')"
            />
            <h3
              class="leading-5 duration-300"
              :class="[
                activeTab === 'uploads' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              {{ $t('coach.demo_section.title') }}
            </h3>
          </div>
          <div
            ref="proSeriesDiv"
            class="py-2 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start"
            @click="updateActiveTab('pro_series')"
          >
            <img
              src="../../assets/icons/hltv.svg"
              :class="[activeTab === 'pro_series' ? 'brightness-200' : '']"
              class="h-4 w-4 duration-300"
              :alt="$t('coach.alt.pro_series_icon')"
            />
            <h3
              class="leading-5 duration-300"
              :class="[
                activeTab === 'pro_series' ? 'text-text-primary' : 'text-text-secondary',
              ]"
            >
              HLTV
            </h3>
          </div>
          <div class="absolute flex-row md:flex bottom-0">
            <div
              class="h-[2px] grow-transition"
              :style="{ width: `${getSectionUnderlinePadding()}px` }"
            />
            <div
              ref="sectionUnderline"
              class="h-[2px] bg-primary-600 grow-transition"
              :style="{ width: `${getSectionUnderlineWidth()}px` }"
            />
          </div>
        </nav>
      </div>

      <div v-if="activeTab === 'history'">
        <MatchSection />
      </div>

      <div v-if="activeTab === 'uploads'">
        <DemoSection />
      </div>

      <div v-if="activeTab === 'pro_series'">
        <ProSeriesList />
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  ref, watch, onMounted, onUnmounted, nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Dashboard from '../../components/layouts/DashboardLayout.vue';
import DemoSection from '../../components/Coach/Matches/display/DemoSection.vue';
import MatchSection from '../../components/Coach/Matches/lists/MatchSection.vue';
import ProSeriesList from '../../components/Coach/Matches/lists/ProSeriesList.vue';
import Info from '../../components/General/Info.vue';

const { t } = useI18n();
useHead({ title: () => t('coach.matches_page.head_title') });

const auth = useAuth();
const user = ref(auth.user());
const route = useRoute();
const router = useRouter();

const activeTab = ref(route.query.tab || 'history');

// Valid tab options
const validTabs = ['history', 'uploads', 'pro_series'];

// Watch for URL changes and validate tab
watch(() => route.query.tab, (newTab) => {
  if (newTab && validTabs.includes(newTab)) {
    activeTab.value = newTab;
    updateUnderlineStyle();
  } else if (newTab && !validTabs.includes(newTab)) {
    // Invalid tab, redirect to history
    router.replace({ query: { ...route.query, tab: 'history' } });
  }
});
const sectionUnderlineWidth = ref(124);
const sectionUnderlinePadding = ref(0);
const historyDiv = ref(null);
const uploadsDiv = ref(null);
const proSeriesDiv = ref(null);

function updateActiveTab(value) {
  activeTab.value = value;
  if (value === 'history') {
    // Remove tab parameter for history (default)
    const {
      tab, teams, events, ...otherQuery
    } = route.query;
    router.push({ query: otherQuery });
  } else if (value === 'uploads') {
    const { teams, events, ...otherQuery } = route.query;
    router.push({ query: { ...otherQuery, tab: value } });
  } else {
    // Add or update tab parameter
    router.push({ query: { ...route.query, tab: value } });
  }
  updateUnderlineStyle();
}

function updateUnderlineStyle() {
  // Small delay to ensure DOM elements are fully rendered
  setTimeout(() => {
    if (activeTab.value === 'history') {
      sectionUnderlineWidth.value = historyDiv.value?.offsetWidth;
      sectionUnderlinePadding.value = historyDiv.value?.offsetLeft;
    } else if (activeTab.value === 'uploads') {
      sectionUnderlineWidth.value = uploadsDiv.value?.offsetWidth;
      sectionUnderlinePadding.value = uploadsDiv.value?.offsetLeft;
    } else if (activeTab.value === 'pro_series') {
      sectionUnderlineWidth.value = proSeriesDiv.value?.offsetWidth;
      sectionUnderlinePadding.value = proSeriesDiv.value?.offsetLeft;
    }
  }, 10);
}

function getSectionUnderlineWidth() {
  return sectionUnderlineWidth.value;
}

function getSectionUnderlinePadding() {
  return sectionUnderlinePadding.value;
}

// Debounced resize handler to prevent excessive calls
let resizeTimeout;
function handleResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    updateUnderlineStyle();
  }, 100);
}

// Update underline on mount to handle direct URL navigation
onMounted(() => {
  nextTick(() => {
    updateUnderlineStyle();
  });

  // Add window resize listener
  window.addEventListener('resize', handleResize);
});

// Cleanup resize listener on unmount
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  clearTimeout(resizeTimeout);
});
</script>
